import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link,
  Paper,
  styled,
  FormControl,
  Avatar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowBack,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import useAxios from "../useAxios";
const AuthCard = styled(Paper)(({ theme }) => ({
  maxWidth: 460,
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: 20,
  backgroundColor: "rgba(255,255,255,0.8)",
  backdropFilter: "blur(12px)",
  boxShadow: "0 10px 40px rgba(183,28,28,0.25)",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    maxWidth: "90%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(2),
    borderRadius: 12,
  },
}));

export default function ForgotPassword() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
   const location = useLocation();
const mobile = location.state?.mobile_number;
  const axiosData = useAxios();
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [user, setUser] = useState({
    email: "",
    password: "",
    mobile: "",
    confirmPassword: "",
  });
  const isStrongPassword = (password) => password.length >= 6;
  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
    const { name, value } = e.target;
    if (!isStrongPassword(user.password)) {
      setPasswordError("Please enter minimum 6 characters");
      return;
    }
    setPasswordError("");
    // Check confirm password mismatch
    if (name === "confirmPassword" || name === "password") {
      if (
        (name === "confirmPassword" && value !== user.password) ||
        (name === "password" &&
          user.confirmPassword &&
          user.confirmPassword !== value)
      ) {
        setConfirmPasswordError("Passwords do not match");
      } else if (
        (name === "password" && value !== user.password) ||
        (name === "password" && user.password && user.password !== value)
      ) {
        setPasswordError("");
      } else {
        setPasswordError("");
        setConfirmPasswordError("");
      }
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (user.password !== user.confirmPassword) {
      setError("Passwords do not match!");
      setLoading(false);
      return;
    }
    if (user.password < 6) {
      setError("Password must be at least 6 characters long!");
      setLoading(false);
      return;
    }
    try {
      // Simulate API call for password reset
      const response = await axiosData.put(`getUser`, {
        mobile_number: parseInt(mobile),
        password: user.password,
      });
      if (response) {
        setSuccessDialogOpen(true);
        setUser({
          email: "",
          password: "",
          mobile: "",
          confirmPassword: "",
        });
      }
    } catch (err) {
      setError("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      user.password.trim() !== "" &&
      isStrongPassword(user.password) &&
      user.confirmPassword.trim() !== "" &&
      confirmPasswordError === "" &&
      passwordError === "" &&
      user.password.length >= 6 &&
      user.password === user.confirmPassword &&
      !loading
    );
  };

  const logoWidth = isXs ? "85%" : isSm ? "75%" : "60%";
  const inputSize = isXs ? "small" : "medium";
  const buttonSize = isXs ? "small" : isSm ? "medium" : "large";
  const typographyVariant = isXs ? "body2" : "body1";
  const spacing = isXs ? 2 : isSm ? 2.5 : 3;

  return (
    <Box
      sx={{
        //minHeight: '100vh',
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `linear-gradient(to bottom right, rgba(245, 188, 188, 0.85), rgba(236, 117, 109, 0.8)), url('/images/auth-bg.jpg')`,
        backgroundSize: "cover",
        // p: 2,
      }}
    >
      <motion.div
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          padding: isXs
            ? theme.spacing(1)
            : isSm
            ? theme.spacing(1)
            : theme.spacing(0),
        }}
      >
        <AuthCard>
          <Box textAlign="center" mb={spacing}>
            <Avatar
              src="/Logo2.png"
              variant="square"
              sx={{
                width: logoWidth,
                mx: "auto",
                height: "auto",
                maxWidth: 300,
                mb: 1,
              }}
            />
            <Typography
              variant="h5"
              fontWeight="600"
              color="text.primary"
              gutterBottom
            >
              Reset Your Password
            </Typography>
            <Typography
              color="text.secondary"
              variant={typographyVariant}
              sx={{ mt: 0.5 }}
            >
              Enter your new password below
            </Typography>
          </Box>

          <form onSubmit={handleReset}>
            <FormControl fullWidth margin="normal">
              <TextField
                name="password"
                label="New Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                placeholder="Enter your new password"
                required
                value={user.password}
                onChange={handleChange}
                error={!!passwordError}
                helperText={passwordError}
                size={inputSize}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" fontSize={inputSize} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size={inputSize}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize={inputSize} />
                        ) : (
                          <Visibility fontSize={inputSize} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 12,
                  },
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow:
                      "0 0 0 1000px transparent inset !important",
                    WebkitTextFillColor: "#000 !important",
                    transition: "background-color 5000s ease-in-out 0s",
                    caretColor: "#000",
                  },
                }}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <TextField
                name="confirmPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                placeholder="Confirm your new password"
                required
                value={user.confirmPassword}
                onChange={handleChange}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                size={inputSize}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" fontSize={inputSize} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        size={inputSize}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff fontSize={inputSize} />
                        ) : (
                          <Visibility fontSize={inputSize} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 12,
                  },
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow:
                      "0 0 0 1000px transparent inset !important",
                    WebkitTextFillColor: "#000 !important",
                    transition: "background-color 5000s ease-in-out 0s",
                    caretColor: "#000",
                  },
                }}
              />
            </FormControl>

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{ mt: 1, textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              color="primary"
              size={buttonSize}
              type="submit"
              disabled={!isFormValid()}
              endIcon={loading ? null : <ArrowBack fontSize={inputSize} />}
              sx={{
                mt: spacing,
                py: isXs ? 0.8 : isSm ? 1 : 0.8,
                borderRadius: 12,
                fontSize: isXs ? "0.8rem" : isSm ? "0.9rem" : "1rem",
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: "#D32F2F",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#B71C1C",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(211, 47, 47, 0.5)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Reset Password"
              )}
            </Button>

            <Box textAlign="center" mt={spacing}>
              <Typography variant={isXs ? "caption" : "body2"}>
                Remember your password?{" "}
                <Link
                  component="button"
                  variant={isXs ? "caption" : "body2"}
                  underline="always"
                  color="primary"
                  fontWeight={500}
                  onClick={() => navigate("/auth", { state: { page: "forget" } })}
                  sx={{
                    fontSize: isXs ? "0.75rem" : "inherit",
                  }}
                >
                  Back to Login
                </Link>
              </Typography>
            </Box>
          </form>
          <Dialog
            open={successDialogOpen}
            //onClose={() => setSuccessDialogOpen(false)}
            PaperProps={{
              sx: {
                borderRadius: "16px",
                padding: "24px",
                textAlign: "center",
                maxWidth: "450px",
              },
            }}
          >
            <Box textAlign="center" mb={2}>
              <CheckCircleOutlineIcon
                color="success"
                sx={{ fontSize: 80, color: "#4caf50" }}
              />
            </Box>

            <DialogTitle
              sx={{
                fontSize: "1.8rem",
                fontWeight: 700,
                color: "#2e7d32",
                padding: 0,
                mb: 2,
              }}
            >
              Reset Successful!
            </DialogTitle>

            <DialogContent sx={{ padding: 0, mb: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                }}
              >
                Your password has been reseted successfully. You can now sign in
                and start exploring.
              </Typography>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", padding: 0 }}>
              <Button
                onClick={() => {
                  setSuccessDialogOpen(false);
                  setConfirmPasswordError("");
                  navigate("/auth", { state: { page: "forget" } })
                }}
                color="success"
                variant="contained"
                size="large"
                sx={{
                  borderRadius: "12px",
                  padding: "10px 24px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                    backgroundColor: "#388e3c",
                  },
                }}
              >
                Continue to Sign In
              </Button>
            </DialogActions>
          </Dialog>
        </AuthCard>
      </motion.div>
    </Box>
  );
}
